/**
 * "Buy now" on product pages: a link to a cart permalink (/cart/{variant}:{quantity}), which
 * starts checkout with only this product. Without JavaScript it buys the variant that was selected
 * when the page loaded. With JavaScript it reads the shade and quantity chosen at the moment of the
 * click, and mirrors the Add to cart button's disabled state for sold-out or unavailable variants.
 */

const LINK_SELECTOR = '[data-buy-now]';

/** @param {HTMLAnchorElement} link */
const getForm = (link) => link.closest('form');

/** @param {HTMLAnchorElement} link */
const getAddButton = (link) => getForm(link)?.querySelector('.add-to-cart-button');

/** @param {HTMLAnchorElement} link */
const syncDisabled = (link) => {
  const disabled = getAddButton(link)?.matches(':disabled') ?? false;
  link.toggleAttribute('aria-disabled', disabled);
  if (disabled) link.setAttribute('aria-disabled', 'true');
};

/** @param {MouseEvent} event */
const handleClick = (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const link = target?.closest(LINK_SELECTOR);
  if (!(link instanceof HTMLAnchorElement)) return;

  event.preventDefault();
  if (link.getAttribute('aria-disabled') === 'true') return;

  const form = getForm(link);
  const variantInput = form?.querySelector('input[name="id"]');
  const quantityInput = form?.querySelector('input[name="quantity"]');
  const variantId = variantInput instanceof HTMLInputElement ? variantInput.value : '';
  const quantity =
    quantityInput instanceof HTMLInputElement ? Math.max(1, Number.parseInt(quantityInput.value, 10) || 1) : 1;

  if (!variantId) {
    window.location.assign(link.href);
    return;
  }

  window.location.assign(`${link.dataset.cartUrl}/${variantId}:${quantity}`);
};

document.addEventListener('click', handleClick);

/** @type {WeakSet<Element>} */
const watched = new WeakSet();

const watchLinks = () => {
  for (const link of document.querySelectorAll(LINK_SELECTOR)) {
    if (!(link instanceof HTMLAnchorElement) || watched.has(link)) continue;
    watched.add(link);
    syncDisabled(link);

    const addButton = getAddButton(link);
    if (addButton) {
      new MutationObserver(() => syncDisabled(link)).observe(addButton, {
        attributes: true,
        attributeFilter: ['disabled'],
      });
    }
  }
};

watchLinks();

/* Variant changes can re-render the buy buttons */
new MutationObserver(watchLinks).observe(document.body, { childList: true, subtree: true });
